import { colors } from '@casavo/base-ui'
import useTranslation from 'next-translate/useTranslation'
import React from 'react'

import { Filter, FilterByArea, FilterByRooms } from '@/types/graphql-generated-types'
import filtersConfig from '@/utils/filters-configuration'
import filtersMapper from '@/utils/filters-mapper'

import { featureList, lightP, pointShapeDivider } from '../styles.css'

const getTranslateString = (type, name): string => {
  const otherFilterConfiguration = filtersConfig.others()
  const values = otherFilterConfiguration.find((c) => c.name === type)?.values || []
  return values.find((v) => v.key === name)?.value || ''
}

type RangeNumber = {
  component: React.FC<FilterRangeProps | FilterNumberProps>
  label: string
  value: FilterRangeProps['value'] | FilterNumberProps['value']
}

type ArrayServices = {
  component: React.FC<FilterArrayStringProps | FilterServicesProps>
  filterName: keyof Filter
}

const FeaturesList: React.FC<{ filters: Filter }> = ({ filters }) => {
  const { t } = useTranslation()

  const listRangeNumber: Array<RangeNumber> = [
    {
      component: FilterRange,
      label: 'mq',
      value: filters.byArea,
    },
    {
      component: FilterRange,
      label: `${t(filters.byRooms?.to > 1 || filters.byRooms?.from > 1 ? 'filter:rooms' : 'filter:room')}`,
      value: filters.byRooms,
    },

    {
      component: FilterNumber,
      label: `${t(filters.byBedrooms?.from > 1 ? 'filter:bedrooms' : 'filter:bedroom')}`,
      value: filters.byBedrooms?.from,
    },
    {
      component: FilterNumber,
      label: `${t(filters.byBathrooms?.from > 1 ? 'filter:bathrooms' : 'filter:bathroom')}`,
      value: filters.byBathrooms?.from,
    },
  ]

  const listArrayServices: Array<ArrayServices> = [
    {
      component: FilterArrayString,
      filterName: 'byFloor',
    },
    {
      component: FilterArrayString,
      filterName: 'byStateOfMaintenance',
    },
    {
      component: FilterArrayString,
      filterName: 'byStateAtDeed',
    },
    { component: FilterServices, filterName: 'onlyWithElevator' },
    { component: FilterServices, filterName: 'onlyWithParking' },
    { component: FilterServices, filterName: 'onlyWithBox' },
    { component: FilterServices, filterName: 'onlyWithBasement' },
    { component: FilterServices, filterName: 'onlyWithGarden' },
    { component: FilterServices, filterName: 'onlyWithBalcony' },
    { component: FilterServices, filterName: 'onlyWithTerrace' },
    { component: FilterServices, filterName: 'onlyWithPool' },
    { component: FilterServices, filterName: 'onlyWithCoolingSystem' },
    { component: FilterServices, filterName: 'onlyWithConciergeService' },
    { component: FilterArrayString, filterName: 'byHeatingSystem' },
  ]

  const hasFeatures = () =>
    listRangeNumber.some((filter) => !!filter.value) ||
    listArrayServices.some((filter) => !!filters[filter.filterName]) ||
    filters?.byConstructionYear

  return (
    <>
      {hasFeatures() && (
        <div>
          <p className={lightP}>{t('save-search:features_title')}</p>
          <p className={featureList} data-testid="features-list">
            {listRangeNumber.map((filter, i) => (
              <filter.component key={i} label={filter.label} value={filter.value} />
            ))}

            {listArrayServices.map((filter, i) => (
              <filter.component key={i} filterName={filter.filterName} filters={filters} />
            ))}

            <FilterRange value={filters.byConstructionYear} />
          </p>
        </div>
      )}
    </>
  )
}

const FilterDivider = () => {
  return <div className={pointShapeDivider} color={colors.greyscale600} />
}

type FilterRangeProps = {
  label?: string
  value: FilterByArea | FilterByRooms | FilterByRooms['from'] | FilterByRooms['to']
}

export const FilterRange: React.FC<FilterRangeProps> = ({ label, value }) => {
  const { t } = useTranslation()

  return value ? (
    <>
      {filtersMapper.formatRange(value.from, value.to, t)} {label}
      <FilterDivider />
    </>
  ) : null
}

type FilterNumberProps = {
  label: string
  value: number
}
export const FilterNumber: React.FC<FilterNumberProps> = ({ label, value }) =>
  value ? (
    <>
      {value} {label}
      <FilterDivider />
    </>
  ) : null

type FilterArrayStringProps = {
  filterName: string
  filters: Filter
}

export const FilterArrayString: React.FC<FilterArrayStringProps> = ({ filterName, filters }) => {
  const { t } = useTranslation()
  const translatedValue = filters[filterName]?.map((v) => t(`filter:${getTranslateString(filterName, v)}`))
  return translatedValue ? (
    <>
      {translatedValue.join(', ')}
      <FilterDivider />
    </>
  ) : null
}

type FilterServicesProps = {
  filterName: string
  filters: Filter
}

export const FilterServices: React.FC<FilterServicesProps> = ({ filterName, filters }) => {
  const servicesValues = filtersConfig.getServicesValues().reduce((acc, cur) => {
    acc[cur.key] = cur
    return acc
  }, {})
  const { t } = useTranslation()
  return filters[filterName] ? (
    <>
      {t(`filter:${servicesValues[filterName].value}`)}
      <FilterDivider />
    </>
  ) : null
}

export default FeaturesList
